import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Lock, Banknote, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WATER_SLUG = "bacteriostatic-water";

const COUNTRIES = ["United States", "United Kingdom", "Canada", "Australia", "Germany", "France", "Netherlands", "Switzerland", "Sweden", "Norway", "Other"];

export default function Checkout() {
    const navigate = useNavigate();
    const { items, subtotal, clear } = useCart();
    const [submitting, setSubmitting] = useState(false);
    const [bundleDiscount, setBundleDiscount] = useState(0);

    const [customer, setCustomer] = useState({
        full_name: "",
        email: "",
        phone: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "United States",
        notes: "",
    });

    useEffect(() => {
        // Compute live discount based on qualified items (excluding bacteriostatic water)
        const qualified = items.reduce((sum, i) => {
            // Find if item is bacteriostatic water
            const isWater = (i.name || "").toLowerCase().includes("bacteriostatic");
            return isWater ? sum : sum + i.quantity;
        }, 0);
        let pct = 0;
        if (qualified >= 10) pct = 30;
        else if (qualified >= 5) pct = 20;
        else if (qualified >= 3) pct = 10;
        setBundleDiscount(pct);
    }, [items]);

    const totals = useMemo(() => {
        const discountAmount = +(subtotal * (bundleDiscount / 100)).toFixed(2);
        const shipping = subtotal >= 250 ? 0 : 25;
        return {
            subtotal,
            discountAmount,
            shipping,
            total: +(subtotal - discountAmount + shipping).toFixed(2),
        };
    }, [subtotal, bundleDiscount]);

    const submit = async (e) => {
        e.preventDefault();
        const required = ["full_name", "email", "address_line1", "city", "postal_code", "country"];
        for (const f of required) {
            if (!customer[f]) {
                toast.error(`Please fill in: ${f.replace("_", " ")}`);
                return;
            }
        }
        if (items.length === 0) {
            toast.error("Your cart is empty");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/orders`, {
                customer,
                items,
                bundle_discount_percent: bundleDiscount,
                payment_method: "bank_transfer",
            });
            clear();
            localStorage.removeItem("pepstep_bundle_discount");
            toast.success("Order placed successfully!");
            navigate(`/order/${res.data.order_number}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Order failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="pt-32 pb-24 min-h-screen flex items-center" data-testid="checkout-empty">
                <div className="max-w-md mx-auto text-center px-6">
                    <h1 className="font-heading text-4xl text-pep-navy font-medium">Your cart is empty</h1>
                    <p className="text-pep-muted mt-3">Add a few peptides before heading to checkout.</p>
                    <Link
                        to="/all"
                        className="inline-block mt-8 bg-pep-primary text-white rounded-full px-6 py-3 font-medium hover:bg-pep-navy transition"
                    >
                        Browse Products
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-28 pb-24" data-testid="checkout-page">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Link to="/all" className="inline-flex items-center gap-2 text-pep-muted hover:text-pep-navy mb-6 text-sm">
                    <ArrowLeft className="w-4 h-4" /> Continue shopping
                </Link>
                <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-pep-navy font-medium mb-12">Checkout</h1>

                <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6">
                        <Section title="Contact information">
                            <Field label="Email *">
                                <Input
                                    type="email"
                                    value={customer.email}
                                    onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                                    placeholder="you@lab.com"
                                    className="rounded-xl bg-white border-pep-border"
                                    data-testid="checkout-email"
                                />
                            </Field>
                            <Field label="Phone (optional)">
                                <Input
                                    value={customer.phone}
                                    onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                                    placeholder="+1 555 ..."
                                    className="rounded-xl bg-white border-pep-border"
                                    data-testid="checkout-phone"
                                />
                            </Field>
                        </Section>

                        <Section title="Shipping address">
                            <Field label="Full name *">
                                <Input
                                    value={customer.full_name}
                                    onChange={(e) => setCustomer({ ...customer, full_name: e.target.value })}
                                    className="rounded-xl bg-white border-pep-border"
                                    data-testid="checkout-name"
                                />
                            </Field>
                            <Field label="Address line 1 *">
                                <Input
                                    value={customer.address_line1}
                                    onChange={(e) => setCustomer({ ...customer, address_line1: e.target.value })}
                                    className="rounded-xl bg-white border-pep-border"
                                    data-testid="checkout-address1"
                                />
                            </Field>
                            <Field label="Address line 2">
                                <Input
                                    value={customer.address_line2}
                                    onChange={(e) => setCustomer({ ...customer, address_line2: e.target.value })}
                                    className="rounded-xl bg-white border-pep-border"
                                    data-testid="checkout-address2"
                                />
                            </Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="City *">
                                    <Input
                                        value={customer.city}
                                        onChange={(e) => setCustomer({ ...customer, city: e.target.value })}
                                        className="rounded-xl bg-white border-pep-border"
                                        data-testid="checkout-city"
                                    />
                                </Field>
                                <Field label="State / Region">
                                    <Input
                                        value={customer.state}
                                        onChange={(e) => setCustomer({ ...customer, state: e.target.value })}
                                        className="rounded-xl bg-white border-pep-border"
                                        data-testid="checkout-state"
                                    />
                                </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Postal code *">
                                    <Input
                                        value={customer.postal_code}
                                        onChange={(e) => setCustomer({ ...customer, postal_code: e.target.value })}
                                        className="rounded-xl bg-white border-pep-border"
                                        data-testid="checkout-postal"
                                    />
                                </Field>
                                <Field label="Country *">
                                    <Select value={customer.country} onValueChange={(v) => setCustomer({ ...customer, country: v })}>
                                        <SelectTrigger className="rounded-xl bg-white border-pep-border" data-testid="checkout-country">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                            <Field label="Order notes (optional)">
                                <Textarea
                                    rows={3}
                                    value={customer.notes}
                                    onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                                    className="rounded-xl bg-white border-pep-border"
                                    data-testid="checkout-notes"
                                />
                            </Field>
                        </Section>

                        <Section title="Payment method">
                            <div className="p-5 rounded-2xl border-2 border-pep-primary bg-pep-primary/5 flex items-start gap-4" data-testid="checkout-payment-method">
                                <div className="w-10 h-10 rounded-xl bg-pep-primary text-white flex items-center justify-center flex-shrink-0">
                                    <Banknote className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-medium text-pep-navy">Bank Transfer</p>
                                    <p className="text-sm text-pep-muted mt-1">
                                        Place your order — we'll email you bank details and your unique reference number.
                                        Your order ships as soon as payment is received (typically 1-2 business days).
                                    </p>
                                </div>
                            </div>
                            <p className="text-xs text-pep-muted mt-3">
                                PayPal will be available soon as an additional payment option.
                            </p>
                        </Section>
                    </div>

                    {/* Summary */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 bg-pep-surface rounded-3xl p-6 border border-pep-border/50" data-testid="checkout-summary">
                            <h3 className="font-heading text-xl font-medium text-pep-navy mb-4">Order summary</h3>
                            <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                                {items.map((i) => (
                                    <div key={i.product_id} className="flex gap-3" data-testid={`checkout-item-${i.product_id}`}>
                                        <div className="w-14 h-14 rounded-xl bg-white overflow-hidden flex-shrink-0 relative">
                                            {i.image && <img src={i.image} alt={i.name} className="w-full h-full object-cover" />}
                                            <span className="absolute -top-1 -right-1 bg-pep-navy text-white text-[10px] rounded-full min-w-5 h-5 px-1 flex items-center justify-center">
                                                {i.quantity}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-pep-navy font-medium leading-snug">{i.name}</p>
                                            <p className="text-xs text-pep-muted">${i.price.toFixed(2)} × {i.quantity}</p>
                                        </div>
                                        <p className="text-sm font-medium text-pep-navy">${(i.price * i.quantity).toFixed(2)}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-6 space-y-2 pt-4 border-t border-pep-border/50">
                                <div className="flex justify-between text-sm">
                                    <span className="text-pep-muted">Subtotal</span>
                                    <span data-testid="checkout-subtotal">${totals.subtotal.toFixed(2)}</span>
                                </div>
                                {bundleDiscount > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-pep-primary">Bundle discount ({bundleDiscount}%)</span>
                                        <span className="text-pep-primary" data-testid="checkout-discount">−${totals.discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-pep-muted">
                                        Shipping{totals.shipping === 0 && <span className="text-pep-primary"> · Free over $250</span>}
                                    </span>
                                    <span data-testid="checkout-shipping">
                                        {totals.shipping === 0 ? "FREE" : `$${totals.shipping.toFixed(2)}`}
                                    </span>
                                </div>
                                <div className="flex justify-between font-heading text-xl pt-3 border-t border-pep-border/50">
                                    <span className="text-pep-navy">Total</span>
                                    <span className="text-pep-navy" data-testid="checkout-total">${totals.total.toFixed(2)}</span>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="mt-6 w-full bg-pep-primary text-white rounded-full py-4 font-medium hover:bg-pep-navy transition disabled:opacity-50 flex items-center justify-center gap-2"
                                data-testid="checkout-submit"
                            >
                                {submitting ? "Placing order..." : (<><Lock className="w-4 h-4" /> Place Order</>)}
                            </button>

                            <div className="mt-4 flex items-start gap-2 text-xs text-pep-muted">
                                <ShieldCheck className="w-4 h-4 text-pep-primary flex-shrink-0 mt-0.5" />
                                Your data is encrypted in transit. We never share customer information.
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-pep-border/50">
            <h3 className="font-heading text-xl font-medium text-pep-navy mb-5">{title}</h3>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-sm text-pep-navy font-medium mb-1.5 block">{label}</label>
            {children}
        </div>
    );
}
